/**
 * ImportadorExcel - Modal para importar movimientos de tesorería desde Excel
 * Permite seleccionar archivo, configurar modo de duplicados, y ver resultados
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Typography,
  Paper, Grid, Chip
} from '@mui/material';
import {
  Upload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import tesoreriaService from '../../services/tesoreriaService';

const ImportadorExcel = ({
  open = false,
  onClose = () => {},
  rutEmpresa = '',
  dvEmpresa = '',
  onImportExitoso = () => {},
  anioDefault = new Date().getFullYear(),
  mesDefault = new Date().getMonth() + 1
}) => {
  const [archivo, setArchivo] = useState(null);
  const [modoDuplicados, setModoDuplicados] = useState('IGNORAR');
  const [periodo, setPeriodo] = useState('');
  const [cargando, setCargando] = useState(false);
  const [resultadoImportacion, setResultadoImportacion] = useState(null);
  const [errorImportacion, setErrorImportacion] = useState('');

  // Validar formato de período (YYYYMM)
  const validarPeriodo = (valor) => {
    if (!valor) return true; // Período es opcional
    const regex = /^\d{6}$/;
    return regex.test(valor);
  };

  // Manejar selección de archivo
  const handleArchivoSeleccionado = (event) => {
    const file = event.target.files[0];
    if (file) {
      const esExcel = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                       'application/vnd.ms-excel'].includes(file.type);

      if (!esExcel) {
        setErrorImportacion('Por favor selecciona un archivo Excel (.xlsx o .xls)');
        setArchivo(null);
        return;
      }

      setArchivo(file);
      setErrorImportacion('');
    }
  };

  // Manejar importación
  const handleImportar = async () => {
    // Validar
    if (!archivo) {
      setErrorImportacion('Debes seleccionar un archivo');
      return;
    }

    if (periodo && !validarPeriodo(periodo)) {
      setErrorImportacion('El período debe tener formato YYYYMM (ej: 202410)');
      return;
    }

    setCargando(true);
    setErrorImportacion('');
    setResultadoImportacion(null);

    try {
      const response = await tesoreriaService.importarArchivo(
        archivo,
        rutEmpresa,
        dvEmpresa,
        periodo || null,
        modoDuplicados
      );

      // Verificar si la importación fue exitosa
      if (!response.exitoso) {
        // Error de validación - mostrar en la UI
        let mensajeError = response.mensaje || 'Error al importar archivo';
        if (response.errores && response.errores.length > 0) {
          const listaErrores = Array.isArray(response.errores)
            ? response.errores.join('\n')
            : String(response.errores);
          mensajeError = `${mensajeError}\n\n${listaErrores}`;
        }
        setErrorImportacion(mensajeError);
        return;
      }

      // Importación exitosa
      setResultadoImportacion(response);

      // Llamar callback para recargar datos
      if (response.cantidad_importados > 0 || response.cantidad_actualizados > 0) {
        onImportExitoso();
      }
    } catch (error) {
      // Error inesperado de red/servidor
      let mensajeError = 'Error al importar archivo';

      if (error.message) {
        mensajeError = error.message;
      }

      setErrorImportacion(mensajeError);
    } finally {
      setCargando(false);
    }
  };

  // Descargar plantilla de Excel
  const handleDescargarPlantilla = async () => {
    try {
      const response = await fetch('/api/v1/importacion/descargar-plantilla');
      if (!response.ok) throw new Error('Error al descargar plantilla');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_tesoreria.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setErrorImportacion('Error al descargar la plantilla: ' + error.message);
    }
  };

  // Limpiar estado al cerrar
  const handleCerrar = () => {
    setArchivo(null);
    setModoDuplicados('IGNORAR');
    setPeriodo('');
    setResultadoImportacion(null);
    setErrorImportacion('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCerrar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Importar Movimientos de Tesorería
        <Button
          size="small"
          onClick={handleCerrar}
          disabled={cargando}
          sx={{ minWidth: 'auto' }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {!resultadoImportacion ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Error general */}
            {errorImportacion && (
              <Alert severity="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {errorImportacion}
              </Alert>
            )}

            {/* Selección de archivo */}
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: '#2196f3',
                  backgroundColor: 'rgba(33, 150, 243, 0.05)'
                }
              }}
              component="label"
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleArchivoSeleccionado}
                style={{ display: 'none' }}
                disabled={cargando}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <UploadIcon sx={{ fontSize: 48, color: '#2196f3' }} />
                {archivo ? (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {archivo.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Haz clic para cambiar archivo
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Haz clic aquí o arrastra un archivo
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Formatos soportados: .xlsx, .xls
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Modo de duplicados */}
            <FormControl fullWidth>
              <InputLabel>Modo de Duplicados</InputLabel>
              <Select
                value={modoDuplicados}
                label="Modo de Duplicados"
                onChange={(e) => setModoDuplicados(e.target.value)}
                disabled={cargando}
              >
                <MenuItem value="IGNORAR">
                  Ignorar (no importar duplicados)
                </MenuItem>
                <MenuItem value="ACTUALIZAR">
                  Actualizar (sobrescribir duplicados)
                </MenuItem>
                <MenuItem value="RECHAZAR">
                  Rechazar (fallar si hay duplicados)
                </MenuItem>
              </Select>
            </FormControl>

            {/* Período opcional */}
            <TextField
              label="Período (Opcional)"
              placeholder="YYYYMM (ej: 202410)"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              disabled={cargando}
              helperText="Deixa vacío para detectar automáticamente de los datos del archivo"
              size="small"
            />

            {/* Botón para descargar plantilla */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleDescargarPlantilla}
              disabled={cargando}
              sx={{ alignSelf: 'flex-start' }}
            >
              📥 Descargar Plantilla Excel
            </Button>

            {/* Información de ayuda */}
            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                <strong>Columnas obligatorias del Excel:</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ ml: 1 }}>
                • Cuenta Banco
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ ml: 1 }}>
                • Fecha (DD/MM/YYYY)
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ ml: 1 }}>
                • Folio
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ ml: 1 }}>
                • Ingreso (número entero)
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ ml: 1 }}>
                • Egreso (número entero)
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ ml: 1 }}>
                • Glosa
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, mt: 1 }}>
                <strong>Columnas opcionales:</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ ml: 1 }}>
                • Referencia, Cuenta, Centro, Clasificador, Entidad
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 1 }}>
                • Solo Ingreso O Egreso (no ambos)
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div">
                • Solo se importa la primera hoja del archivo
              </Typography>
            </Paper>
          </Box>
        ) : (
          // Mostrar resultado de importación
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Indicador de éxito */}
            {!errorImportacion && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 40 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                    Importación completada exitosamente
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Los movimientos han sido cargados en el sistema
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Errores si los hay */}
            {errorImportacion && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorImportacion}
              </Alert>
            )}

            {/* Estadísticas */}
            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Estadísticas de Importación
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                      {resultadoImportacion.cantidad_importados || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Nuevos importados
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                      {resultadoImportacion.cantidad_actualizados || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Actualizados
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                      {resultadoImportacion.cantidad_ignorados || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Ignorados (duplicados)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                      {resultadoImportacion.cantidad_errores || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Errores
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Detalles de errores si existen */}
            {resultadoImportacion.errores && resultadoImportacion.errores.length > 0 && (
              <Paper sx={{ p: 2, backgroundColor: '#ffebee', border: '1px solid #ffcdd2' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#c62828' }}>
                  Errores detectados:
                </Typography>
                <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {resultadoImportacion.errores.map((error, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      display="block"
                      sx={{ mb: 0.5, color: '#d32f2f' }}
                    >
                      • Fila {error.fila}: {error.mensaje}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            )}

            {/* Chips de resumen */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {resultadoImportacion.cantidad_importados > 0 && (
                <Chip
                  label={`${resultadoImportacion.cantidad_importados} nuevos`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              )}
              {resultadoImportacion.cantidad_actualizados > 0 && (
                <Chip
                  label={`${resultadoImportacion.cantidad_actualizados} actualizados`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              {resultadoImportacion.cantidad_ignorados > 0 && (
                <Chip
                  label={`${resultadoImportacion.cantidad_ignorados} ignorados`}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
              {resultadoImportacion.cantidad_errores > 0 && (
                <Chip
                  label={`${resultadoImportacion.cantidad_errores} errores`}
                  color="error"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {!resultadoImportacion ? (
          <>
            <Button onClick={handleCerrar} disabled={cargando}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={cargando ? <CircularProgress size={20} /> : <UploadIcon />}
              onClick={handleImportar}
              disabled={!archivo || cargando || !!errorImportacion}
            >
              {cargando ? 'Importando...' : 'Importar'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleCerrar}>
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportadorExcel;
